// Dependency-free transform palette shared by Node ingestion and web previews.
const iri = (value) => ({ kind: 'iri', value });
const lit = (value) => ({ kind: 'literal', value: String(value) });
const typed = (value, datatype) => ({ kind: 'literal', value: String(value), datatype });
const wrap = (value, output, datatype) => output === 'iri' ? iri(value) : output === 'typed' ? typed(value, datatype) : lit(value);

const PARSERS = {
  number: (raw) => Number(String(raw).replace(/[^0-9.]/g, '')) || 0,
  duration: (raw) => {
    const s = String(raw).toLowerCase();
    return Number((s.match(/(\d+)\s*hour/) ?? [])[1] ?? 0) * 60 + Number((s.match(/(\d+)\s*min/) ?? [])[1] ?? 0);
  },
};

const PALETTE = {
  const: (_raw, _row, a) => [{ kind: 'literal', value: a.value }],
  direct: (raw, _row, a) => String(raw).trim() ? [wrap(String(raw).trim(), a.output ?? 'literal', a.datatype)] : [],
  mapValues: (raw, _row, a) => {
    const trimmed = String(raw).trim();
    if (a.dropEmpty && trimmed === '') return [];
    const key = a.caseInsensitive ? trimmed.toLowerCase() : trimmed;
    const value = a.map?.[key] ?? (a.default === 'drop' ? undefined : a.default);
    return value === undefined ? [] : [wrap(value, a.output ?? 'literal', a.datatype)];
  },
  split: (raw, _row, a) => String(raw).split(a.delimiter).map((s) => s.trim()).filter(Boolean).map(lit),
  dateBucket: (raw, _row, a) => {
    if (a.granularity === 'year') {
      const year = String(raw).slice(0, 4);
      return /^\d{4}$/.test(year) ? [typed(year, 'xsd:gYear')] : [];
    }
    if (a.granularity === 'quarter') {
      const m = String(raw).match(/^(\d{4})-(\d{2})/);
      return m ? [lit(`${m[1]}-Q${Math.floor((Number(m[2]) - 1) / 3) + 1}`)] : [];
    }
    return [];
  },
  bucket: (raw, _row, a) => {
    const n = PARSERS[a.parse ?? 'number'](raw);
    if (!n) return [];
    const i = a.edges.findIndex((edge) => n <= edge);
    return [lit(a.labels[i === -1 ? a.labels.length - 1 : i])];
  },
  rules: (raw, row, a) => {
    for (const rule of a.rules) {
      const ok = rule.when.every((c) => {
        const cell = String((c.col === '@self' ? raw : row[c.col]) ?? '').trim();
        return c.op === 'neq' ? cell !== c.value : cell === c.value;
      });
      if (ok) return [lit(rule.then === '@self' ? raw : rule.then)];
    }
    return a.fallback !== undefined ? [lit(a.fallback)] : [];
  },
};

// A column's transform is either one step or an ordered chain. Written as a bare
// object while it stays a single step, so existing mappings are unchanged; it
// becomes an array only once a second step is added.
export const transformSteps = (transform) =>
  Array.isArray(transform) ? transform : transform ? [transform] : [];

// Collapse back to the bare-object form when a chain has been reduced to one
// step, so what the analyst saves reads the same as what they started from.
export const collapseTransform = (steps) => (steps.length === 1 ? steps[0] : steps);

function runStep(step, incoming, row) {
  const fn = PALETTE[step.fn];
  if (!fn) throw new Error(`Unknown transform fn: ${step.fn}`);
  return fn(step.fn === 'const' ? '' : incoming, row, step);
}

// Each step after the first runs once per value the previous step produced, so
// `split` → `mapValues` coarsens every value the split yielded. Only the final
// step's output/datatype govern the node kind; earlier steps are read for their
// value alone. In `rules`, `@self` is therefore the value entering that step,
// while `col` always reads the original source row.
export function applyColumn(column, row) {
  const steps = transformSteps(column.transform);
  if (!steps.length) throw new Error(`Column ${column.source} has no transform.`);
  let nodes = runStep(steps[0], steps[0].fn === 'const' ? '' : row[column.source] ?? '', row);
  for (const step of steps.slice(1)) nodes = nodes.flatMap((node) => runStep(step, node.value, row));
  return nodes;
}
